import { type CancelTokenSource } from 'axios'
import { type IReactionDisposer, makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { Button } from 'shared/ui'
import { type IResponseComplianceServicesBrand } from 'entities/Compliance'
import type {
  IResponseBusinessInformation,
  IResponseComplianceServicesCampaign,
} from 'entities/Compliance/api/types/complianceType'
import {
  type OrganizationBusinessProfile,
  PointsOfContact,
  type Summary,
} from 'entities/Compliance/model'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { StepsStore } from 'pages/settings/store/stepsStore'
import { type SettingsStore } from 'pages/settings/store/settingsStore'
import { ErrorStore } from './errorStore'
import { AiGenerationStore } from './aiGenerationStore'
import { AISidebarStore } from './aiSidebarStore'

export class ComplianceLayerStore {
  private _stepsStore = new StepsStore()
  private _errorStore = new ErrorStore()
  private _aiGenerationStore = new AiGenerationStore(this)
  private _aiSidebarStore = new AISidebarStore(this)
  private _disposePageReaction: IReactionDisposer | null = null
  private _cancelPageSource: CancelTokenSource | null = null

  private _loading = true

  constructor(private _settingsStore: SettingsStore, private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  get settingsStore() {
    return this._settingsStore
  }

  get loading() {
    return this._loading
  }

  get stepsStore() {
    return this._stepsStore
  }

  get errorStore() {
    return this._errorStore
  }

  get aiGenerationStore() {
    return this._aiGenerationStore
  }

  get aiSidebarStore() {
    return this._aiSidebarStore
  }

  get organizationTollFree() {
    return this._entities.complianceStore.organizationTollFree
  }

  get tollFreeProfileItems() {
    if (!this.organizationTollFree) return []

    return this.organizationTollFree.items
  }

  get complianceServicesList() {
    return this._entities.complianceStore.complianceServicesList
  }

  get complianceServiceCampaign() {
    return this.complianceServicesList?.campaign
  }

  get complianceServiceBrand() {
    return this.complianceServicesList?.brand
  }

  get businessInformation() {
    return this.organizationBusinessProfile?.businessInformation
  }

  get pointsOfContact() {
    if (!this.organizationBusinessProfile) return new PointsOfContact([])

    return this.organizationBusinessProfile.pointsOfContact
  }

  get businessProfile() {
    if (!this.organizationBusinessProfile) return null

    return this.organizationBusinessProfile.businessProfile
  }

  get organizationBusinessProfile() {
    return this._entities.complianceStore.organizationBusinessProfile
  }

  get summary() {
    return this._entities.complianceStore.summary
  }

  get voiceIntegrity() {
    return this._entities.complianceStore.organizationVoiceIntegrity
  }

  get shakenStir() {
    return this._entities.complianceStore.organizationShakenStir
  }

  get organizationCnam() {
    return this._entities.complianceStore.organizationCnam
  }

  get billingComplianceInfo() {
    return this._entities.billingStore.billingComplianceInfo
  }

  get tollFreeMessageVolume() {
    return this._entities.complianceStore.tollFreeMessageVolume
  }

  get tollFreeUseCases() {
    return this._entities.complianceStore.tollFreeUseCases
  }

  get tollFreeUnverifiedNumbers() {
    return this._entities.complianceStore.unverifiedTollFreeNumbers
  }

  get unassignedTollFreeUnverifiedNumbers() {
    return this.tollFreeUnverifiedNumbers.filter((number) => !number.numberable?.name)
  }

  get assignedTollFreeUnverifiedNumbers() {
    return this.tollFreeUnverifiedNumbers.filter((number) => number.numberable?.name)
  }

  get internationalRegulatoryBundlesStore() {
    return this._entities.complianceStore.internationalRegulatoryBundlesStore
  }

  init = async () => {
    runInAction(() => {
      this._loading = true
    })

    await Promise.all([
      this._entities.usersStore.initPromise,
      this._entities.subscriptionStore.initPromise,
      this._entities.complianceStore.fetchSummary(),
      this._entities.complianceStore.fetchOrganizationBusinessProfile(),
      this._entities.complianceStore.fetchComplianceServicesList(),
      this._entities.complianceStore.fetchOrganizationTollFree(),
      this._entities.complianceStore.complianceVoiceServicesStore.loadFreeNumbers(),
      this._entities.complianceStore.fetchOrganizationUKCompliance(),
      this._entities.billingStore.fetchBillingComplianceInfo(),
      this._entities.numbersStore.fetchUnassignedNumbers(),
      this._entities.numbersStore.fetchNumbers(),
      this._entities.tenDlcLimitsStore.fetchLimits(),
    ])

    await this._entities.complianceStore.complianceVoiceServicesStore.fetchComplianceVoiceServicesList()

    this._entities.complianceStore.updateUnverifiedTollFreeNumbers()
    runInAction(() => {
      this._loading = false
    })
  }

  dispose = () => {
    this._disposePageReaction?.()
    this._cancelPageSource?.cancel()
  }

  setBusinessInformation = (businessInformation: IResponseBusinessInformation | null) => {
    this.organizationBusinessProfile?.setBusinessInformation(businessInformation)

    if (businessInformation?.contact_person) {
      this.organizationBusinessProfile?.updateFirstContact(businessInformation.contact_person)
    }
  }

  setOrganizationBusinessProfile = (data: OrganizationBusinessProfile | null) => {
    if (!data) return

    this._entities.complianceStore.setOrganizationBusinessProfile(data)
  }

  setSummary = (summary: Summary | null) => {
    if (!summary) return

    this._entities.complianceStore.setSummary(summary)
  }

  set10DLCServiceCampaign = (data: IResponseComplianceServicesCampaign) => {
    this.complianceServicesList?.syncCampaign(data)
  }

  set10DLCServiceBrand = (data: IResponseComplianceServicesBrand) => {
    this.complianceServiceBrand?.syncData(data)
  }

  getCampaignData = async () => {
    await this._entities.complianceStore.fetchComplianceServicesList()
  }

  loadComplianceTollFreeFormFields = async () => {
    await this._entities.complianceStore.loadTollFreeFormFields()
  }

  preventSubmitModal = () => {
    if (this.businessProfile?.isStatusFailed) {
      const modalId = nanoid()

      modalStore.addModal({
        id: modalId,
        type: ModalTypeList.WARNING,
        title: 'Action forbidden',
        desc: 'This action requires approved Business Profile',
        ModalActions: () => (
          <Button
            typeBtn={'contained'}
            text='Cancel'
            size={'medium'}
            contained={'secondary'}
            onClick={() => modalStore.removeModal(modalId)}
          />
        ),
      })
      return true
    }
    return false
  }
}
